import type { ReactNode } from "react";
import { Icon } from "./Icon.js";
import type { IconName } from "./Icon.js";
import "./EmptyState.css";

/**
 * How much room this state is entitled to take.
 *
 * `panel` is the original: a stacked block with generous padding, for when
 * emptiness is the whole screen and there is nothing to compete with.
 *
 * `inline` is for a state that sits *above* content which still renders — the
 * calendar grid when a month has no events, a table header with no rows. At
 * panel size those pushed the real content off the bottom of the viewport,
 * which is how a helpful message turns into a layout bug.
 */
export type EmptyStateSize = "panel" | "inline";

export interface EmptyStateProps {
  icon?: IconName;
  title: string;
  description?: string;
  action?: ReactNode;
  size?: EmptyStateSize;
  className?: string;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  size = "panel",
  className,
}: EmptyStateProps) {
  const inline = size === "inline";

  return (
    <div
      className={["ui-empty-state", `ui-empty-state--${size}`, className ?? ""]
        .filter(Boolean)
        .join(" ")}
    >
      {icon && (
        <span className="ui-empty-state__icon">
          {/* Scaled with the container: a 26px glyph in a 32px circle is a
              filled disc, not an icon. */}
          <Icon name={icon} size={inline ? 15 : 26} />
        </span>
      )}
      <div className="ui-empty-state__text">
        <p className="ui-empty-state__title">{title}</p>
        {description && <p className="ui-empty-state__description">{description}</p>}
      </div>
      {action && <div className="ui-empty-state__action">{action}</div>}
    </div>
  );
}
